/**
 * Store for `DATA.layers`
 * @module
 */

var _ = require('optimizely-client-fn');
var actionTypes = require('../constants/action_types');
var freeze = require('../utils/freeze');
var Policy = require('../utils/policy');

/**
 * Holds the data for every layer
 */
module.exports = {
  initialize: function() {
    this.__state = {
      layers: {},
      experiments: {},
      variations: {},
    };

    this.on(actionTypes.DATA_LOADED, this.__loadData);
  },

  /**
   * Loads layer data into the store state
   * @param {Object} payload
   * @param {ClientData} payload.data
   * @private
   */
  __loadData: function(payload:any) {
    if (_.isEmpty(payload.data.layers)) {
      return;
    }
    var self = this;
    _.each(payload.data.layers, function(layer) {
      _.each(layer.experiments, function(experiment) {
        if (!layer.pageIds) {
          layer.pageIds = layer.viewIds;
        }
        if (!experiment.campaignName && !Policy.isSingleExperimentPolicy(layer.policy)) {
          experiment.campaignName = layer.name;
        } else if (Policy.isSingleExperimentPolicy(layer.policy) && __GROUPS__ && layer.groupId) {
            experiment.groupId = layer.groupId;
        }
        _.each(experiment.variations, function(variation) {
          _.each(variation.actions, function(action) {
            if (!action.pageId) {
              action.pageId = action.viewId;
            }
          });
          // Note: frozen reference via freeze.deepFreeze(layer) below
          self.__state.variations[variation.id] = variation;
        });
        // Note: frozen reference via freeze.deepFreeze(layer) below
        self.__state.experiments[experiment.id] = experiment;
      });
      freeze.deepFreeze(layer);
      self.__state.layers[layer.id] = layer;
    });
    this.emitChange();
  },

  /**
   * @returns {Layer[]}
   */
  getAll: function(): Layer[] {
    return freeze.safeReference(_.values(this.__state.layers));
  },

  getCampaignsMap: function() {
    return freeze.safeReference(this.__state.layers);
  },

  getExperimentsMap: function() {
    return freeze.safeReference(this.__state.experiments);
  },

  getVariationsMap: function() {
    return freeze.safeReference(this.__state.variations);
  },

  /**
   * @returns {number} number of Layers that exist
   */
  getCount: function(): number {
    return _.keys(this.__state.layers).length;
  },

  /**
   * Get all Layers from a set of pageIds
   *
   * @param {string[]} pageIds
   * @returns {Layer[]}
   */
  getAllByPageIds: function(pageIds:string[]): Layer[] {
    return freeze.safeReference(_.filter(this.__state.layers, function(layer) {
      return _.some(pageIds, _.partial(_.includes, layer.pageIds));
    }));
  },

  /**
   * @param {string} id
   * @returns {Layer|undefined}
   */
  get: function(id:string): Layer|undefined {
    return freeze.safeReference(this.__state.layers[id]);
  },

  /**
   * Gets the layer that contains the given experiment
   * @param {string} experimentId
   * @returns {Layer|undefined}
   */
  getLayerByExperimentId: function(experimentId:string): Layer|undefined {
    var matchingLayer = _.find(this.__state.layers, function(layer) {
      return _.find(layer.experiments, {
        id: experimentId,
      });
    });
    return freeze.safeReference(matchingLayer);
  },

  /**
   * Gets the experiment that contains the given variation
   * @param {string} variationId
   * @returns {Experiment|undefined}
   */
  getExperimentByVariationId: function(variationId:string): Experiment|undefined {
    var matchingExperiment;
    // Use _.some to find the first matchingExperiment
    _.some(this.__state.layers, function(layer) {
      _.some(layer.experiments, function(experiment) {
        if (_.find(experiment.variations, {id: variationId})) {
          matchingExperiment = experiment;
        }
        return matchingExperiment;
      });
      return matchingExperiment;
    });
    return freeze.safeReference(matchingExperiment);
  },
};
